/**
 * The coverage assessment for a client, as an explicit calculation state.
 *
 * Reads the engine's figures from the client the host passes in (the live client, or the plan client
 * Your Plan builds) and from the funding sources the host chooses (live, or a completed guided
 * session's snapshot). Save progress and save failures come from the funding-source save state. The
 * host also sets the flow policy: whether an account is required, whether recorded policies count,
 * and how combined coverage is read.
 */
import { useMemo } from 'react';

import { countCompleteSelfFundingAccounts } from '../lib/account-completeness';
import { deriveFundingAssessment, type FundingAssessment } from '../lib/derive-funding-assessment';
import type { FundingCalculation } from '../lib/calculation-state';
import { useFundingSourcesState } from './use-funding-sources-state';

export interface UseFundingAssessmentArgs {
  client: Client | null | undefined;
  clientStatus: 'loading' | 'ready' | 'failed';
  retryClient: () => void;
  requireSelfFundingAccount: boolean;
  includeRecordedPolicies: boolean;
  /** 'self-plus-policy' sums the snapshot (completed guided sessions); 'engine' reads the client. */
  combinedCoverageRule: 'engine' | 'self-plus-policy';
  /** Funding sources to assess; defaults to the client's live sources. */
  fundingSources?: MultipleFundingSources;
}

export function useFundingAssessment({
  client,
  clientStatus,
  retryClient,
  requireSelfFundingAccount,
  includeRecordedPolicies,
  combinedCoverageRule,
  fundingSources,
}: UseFundingAssessmentArgs): FundingCalculation<FundingAssessment> {
  const { isSaving, saveError } = useFundingSourcesState();

  return useMemo(() => {
    const sources = fundingSources ?? client?.multipleFundingSources;
    const selfFundingCoverage = sources?.multipleSelfFundingProjectedCostCoverage;
    const policyCoverage = sources?.multiplePolicyProjectedCostCoverage;
    const combinedCoverage =
      combinedCoverageRule === 'self-plus-policy'
        ? (selfFundingCoverage ?? 0) + (policyCoverage ?? 0)
        : client?.fundingSources?.combinedProjectedCostCoverage;

    return deriveFundingAssessment({
      clientStatus: client || clientStatus !== 'ready' ? clientStatus : 'loading',
      retryClient,
      saveStatus: saveError ? 'failed' : isSaving ? 'saving' : 'idle',
      retrySave: saveError?.retry ?? retryClient,
      totalLtcCost: client?.allPhaseCosts?.allPhaseInflatedTotalCost,
      yearsTillLtc: client?.appliedInferenceSet?.yearsTillLtc,
      completeAccountCount: countCompleteSelfFundingAccounts(sources?.selfFunding),
      selfFundingCoverage,
      policyCoverage,
      combinedCoverage,
      requireSelfFundingAccount,
      includeRecordedPolicies,
    });
  }, [
    client,
    clientStatus,
    retryClient,
    fundingSources,
    combinedCoverageRule,
    isSaving,
    saveError,
    requireSelfFundingAccount,
    includeRecordedPolicies,
  ]);
}
